import React, { useState, useMemo } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, Button,
  Chip, Divider, InputAdornment, Stack,
} from '@mui/material';
import {
  Assessment, TrendingUp, TrendingDown, AccountBalance, Book, Receipt,
  Payments, CalendarMonth, Search, BarChart, Layers, ChevronRight, ArrowBack,
  DateRange,
} from '@mui/icons-material';
import IncomeStatement from './reports/IncomeStatement';
import TrialBalance from './reports/TrialBalance';
import CashFlow from './reports/CashFlow';
import ReceiptsPayments from './reports/ReceiptsPayments';
import ReceiptsLedger from './reports/ReceiptsLedger';
import WeeklyCashLog from './reports/WeeklyCashLog';
import ExpenseLedger from './reports/ExpenseLedger';
import PaymentsLog from './reports/PaymentsLog';
import BankStatement from './reports/BankStatement';
import BankAnalysis from './reports/BankAnalysis';
import CashBookReport from './reports/CashBookReport';
import ReportErrorBoundary from './reports/ReportErrorBoundary';

const REPORT_CATEGORIES = [
  {
    title: 'Financial Statements',
    caption: 'Formal statements of operations',
    icon: <BarChart />, color: '#0B2545',
    reports: [
      { key: 'income-statement',   name: 'Income Statement',       desc: 'Income vs expense summary for a period',                icon: <TrendingUp />,   accent: '#0F766E' },
      { key: 'receipts-payments',  name: 'Receipts & Payment A/c', desc: 'Cash-basis statement with opening and closing balances', icon: <Receipt />,      accent: '#0B2545' },
      { key: 'trial-balance',      name: 'Trial Balance',          desc: 'All ledger balances at a point in time',                 icon: <Layers />,       accent: '#0B2545' },
      { key: 'cash-flow',          name: 'Cash Flow Summary',      desc: 'Cash movements across bank and hand',                    icon: <AccountBalance />, accent: '#13315C' },
    ],
  },
  {
    title: 'Income Reports',
    caption: 'Detail views of receipts',
    icon: <TrendingUp />, color: '#0F766E',
    reports: [
      { key: 'receipts-ledger',    name: 'Receipts Ledger',      desc: 'Chronological log of every receipt, filterable by type', icon: <Receipt />, accent: '#0F766E' },
      { key: 'receipts-ledger-category', name: 'Receipts Ledger (Category)', desc: 'Receipts grouped by category, multi-select filter', icon: <Assessment />, accent: '#0F766E' },
      { key: 'weekly-cash-log',    name: 'Weekly Cash Log',      desc: 'Full log of every income entry',            icon: <Book />,      accent: '#0F766E' },
    ],
  },
  {
    title: 'Expense Reports',
    caption: 'Detail views of payments',
    icon: <TrendingDown />, color: '#B91C1C',
    reports: [
      { key: 'expense-ledger',          name: 'Expense Ledger',            desc: 'Chronological log of every payment, multi-select filter by type', icon: <Payments />,   accent: '#B91C1C' },
      { key: 'expense-ledger-category', name: 'Expense Ledger (Category)', desc: 'Payments grouped by category, multi-select filter',              icon: <Assessment />, accent: '#B91C1C' },
      { key: 'payment-vouchers',        name: 'Payment Vouchers Log',      desc: 'Weekly grouped register of every payment voucher',               icon: <Receipt />,    accent: '#B91C1C' },
    ],
  },
  {
    title: 'Bank & Cash',
    caption: 'Bank and cash movements',
    icon: <AccountBalance />, color: '#C89211',
    reports: [
      { key: 'bank-statement',   name: 'Bank Statement',    desc: 'Per-account deposits and payments',      icon: <AccountBalance />, accent: '#C89211' },
      { key: 'bank-analysis',    name: 'Bank Analysis',     desc: 'Summary of every bank: b/fwd, deposits, withdrawals, c/fwd', icon: <Layers />, accent: '#C89211' },
      { key: 'cash-book-report', name: 'Cash Book Report',  desc: 'Formatted cash book for a month',        icon: <Book />,          accent: '#C89211' },
    ],
  },
];

const iso = (d) => d.toISOString().slice(0, 10);

const buildPresets = () => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const startOfMonth      = new Date(y, m, 1);
  const endOfMonth        = new Date(y, m + 1, 0);
  const startOfLastMonth  = new Date(y, m - 1, 1);
  const endOfLastMonth    = new Date(y, m, 0);
  const qStart            = new Date(y, Math.floor(m / 3) * 3, 1);
  const qEnd              = new Date(y, Math.floor(m / 3) * 3 + 3, 0);
  const startOfYear       = new Date(y, 0, 1);
  const startOfLastYear   = new Date(y - 1, 0, 1);
  const endOfLastYear     = new Date(y - 1, 11, 31);
  return [
    { key: 'this-month',  label: 'This Month',   start: iso(startOfMonth),     end: iso(endOfMonth) },
    { key: 'last-month',  label: 'Last Month',   start: iso(startOfLastMonth), end: iso(endOfLastMonth) },
    { key: 'quarter',     label: 'This Quarter', start: iso(qStart),           end: iso(qEnd) },
    { key: 'ytd',         label: 'Year to Date', start: iso(startOfYear),      end: iso(now) },
    { key: 'last-year',   label: 'Last Year',    start: iso(startOfLastYear),  end: iso(endOfLastYear) },
  ];
};

const fmtDateShort = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Reports() {
  const now = new Date();
  const [start, setStart]   = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [end, setEnd]       = useState(iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null); // report key

  const presets = useMemo(buildPresets, []);
  const activePreset = presets.find(p => p.start === start && p.end === end)?.key || null;
  const setPreset = (p) => { setStart(p.start); setEnd(p.end); };

  const filtered = REPORT_CATEGORIES
    .map(cat => ({
      ...cat,
      reports: cat.reports.filter(r =>
        !search || `${r.name} ${r.desc}`.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(cat => cat.reports.length > 0);

  const activeReport = REPORT_CATEGORIES.flatMap(c => c.reports).find(r => r.key === active);

  const dateRangeError = start && end && new Date(end) < new Date(start);

  // ─── VIEW: Active report takes over the whole page ────────────────────────
  if (activeReport) {
    return (
      <Box>
        {/* Back bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap',
        }}>
          <Button
            variant="outlined" startIcon={<ArrowBack />} onClick={() => setActive(null)}
            size="small"
            sx={{ color: '#0B2545', borderColor: '#0B2545', '&:hover': { borderColor: '#0B2545', bgcolor: '#0B25450d' } }}
          >
            Back
          </Button>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }} noWrap>
              {activeReport.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              <b>{fmtDateShort(start)}</b> → <b>{fmtDateShort(end)}</b>
            </Typography>
          </Box>
        </Box>

        {/* Date-range filter card */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 1.5,
              bgcolor: 'primary.main', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <DateRange />
            </Box>
            <TextField
              type="date" size="small" label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={start} onChange={e => setStart(e.target.value)}
              error={!!dateRangeError}
              sx={{ minWidth: { xs: 140, sm: 170 }, flex: { xs: 1, sm: 'none' } }}
            />
            <TextField
              type="date" size="small" label="End Date"
              InputLabelProps={{ shrink: true }}
              value={end} onChange={e => setEnd(e.target.value)}
              error={!!dateRangeError}
              helperText={dateRangeError ? 'End must be after start' : ''}
              sx={{ minWidth: { xs: 140, sm: 170 }, flex: { xs: 1, sm: 'none' } }}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 1, display: { xs: 'none', md: 'block' } }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {presets.map(p => (
                <Chip
                  key={p.key} size="small" label={p.label}
                  onClick={() => setPreset(p)}
                  sx={{
                    fontWeight: 600, cursor: 'pointer',
                    bgcolor: activePreset === p.key ? '#0B2545' : '#eef2f6',
                    color:   activePreset === p.key ? '#fff'    : '#0B2545',
                    '&:hover': { bgcolor: activePreset === p.key ? '#13315C' : '#dce3ec' },
                  }}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        {dateRangeError ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">Please pick an end date on or after the start date.</Typography>
          </Box>
        ) : active === 'income-statement' ? (
          <ReportErrorBoundary>
            <IncomeStatement start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'trial-balance' ? (
          <ReportErrorBoundary>
            <TrialBalance start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'cash-flow' ? (
          <ReportErrorBoundary>
            <CashFlow start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'receipts-payments' ? (
          <ReportErrorBoundary>
            <ReceiptsPayments start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'receipts-ledger' ? (
          <ReportErrorBoundary>
            <ReceiptsLedger start={start} end={end} groupBy="type" />
          </ReportErrorBoundary>
        ) : active === 'receipts-ledger-category' ? (
          <ReportErrorBoundary>
            <ReceiptsLedger start={start} end={end} groupBy="category" />
          </ReportErrorBoundary>
        ) : active === 'weekly-cash-log' ? (
          <ReportErrorBoundary>
            <WeeklyCashLog start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'expense-ledger' ? (
          <ReportErrorBoundary>
            <ExpenseLedger start={start} end={end} groupBy="type" />
          </ReportErrorBoundary>
        ) : active === 'expense-ledger-category' ? (
          <ReportErrorBoundary>
            <ExpenseLedger start={start} end={end} groupBy="category" />
          </ReportErrorBoundary>
        ) : active === 'payment-vouchers' ? (
          <ReportErrorBoundary>
            <PaymentsLog start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'bank-statement' ? (
          <ReportErrorBoundary>
            <BankStatement start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'bank-analysis' ? (
          <ReportErrorBoundary>
            <BankAnalysis start={start} end={end} />
          </ReportErrorBoundary>
        ) : active === 'cash-book-report' ? (
          <ReportErrorBoundary>
            <CashBookReport start={start} end={end} />
          </ReportErrorBoundary>
        ) : (
          <Box sx={{
            py: 8, textAlign: 'center',
            border: '2px dashed #CBD5E1', borderRadius: 2, bgcolor: '#fff',
          }}>
            <Assessment sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} color="text.secondary">
              {activeReport.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 520, mx: 'auto' }}>
              This report is not yet available. Tell me what columns and totals you want and I'll wire it up.
            </Typography>
            <Chip
              label="In development"
              size="small" color="warning"
              sx={{ mt: 2, fontWeight: 700, letterSpacing: '0.06em' }}
            />
          </Box>
        )}
      </Box>
    );
  }

  // ─── VIEW: Report browser ─────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        mb: { xs: 2, sm: 3 }, gap: 2, flexWrap: 'wrap',
      }}>
        <Box>
          <Typography variant="caption" color="secondary.main" fontWeight={700} sx={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Insights
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', mt: 0.5 }}>
            Reports Center
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate statements, ledgers, and financial summaries.
          </Typography>
        </Box>
      </Box>

      {/* Search bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'primary.main', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CalendarMonth />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, fontSize: 10 }}>
              Choose a report
            </Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              Pick the date range after selecting one.
            </Typography>
          </Box>

          <TextField
            placeholder="Search reports…" size="small"
            value={search} onChange={e => setSearch(e.target.value)}
            fullWidth
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              maxWidth: { sm: 320 },
              ml: { xs: 0, sm: 'auto' },
              order: { xs: 3, sm: 0 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Report categories */}
      {filtered.length === 0 ? (
        <Card><CardContent><Typography color="text.secondary" align="center">No reports match your search.</Typography></CardContent></Card>
      ) : filtered.map(cat => (
        <Box key={cat.title} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1,
              bgcolor: cat.color, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {cat.icon}
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1}>{cat.title}</Typography>
              <Typography variant="caption" color="text.secondary">{cat.caption}</Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {cat.reports.map(r => (
              <Grid item xs={12} sm={6} md={4} key={r.key}>
                <Card
                  onClick={() => setActive(r.key)}
                  sx={{
                    cursor: 'pointer', height: '100%',
                    transition: 'all 0.18s ease',
                    boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 10px 30px ${r.accent}22`,
                      borderColor: r.accent,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                      <Box sx={{
                        width: 38, height: 38, borderRadius: 1.5,
                        bgcolor: `${r.accent}12`, color: r.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {r.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {r.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {r.desc}
                        </Typography>
                      </Box>
                      <ChevronRight sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}
